import { Box, Button, Input, Popup, Table, TableRow, TextArea, TopBar } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Company,
  Currency,
  Customer,
  InvoicePayment,
  InvoiceState,
  Order,
  Project,
  Receipt,
  createNewInvoicePayment,
  deletePaymentById,
  generateNotification,
  getCompanyById,
  getCurrencyById,
  getCustomerById,
  getPaymentsForInvoice,
  updateInvoice,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { useUser } from '../../components/UserContext';
import {
  convertExistingPaymentsIntoTableEntries,
  generateEmptyInvoicePayment,
  getTotalInvoiceSum,
} from '../../utils/invoice/Invoice.utils';
import { getUserNameForSimpleUser } from '../../utils/user/User.utils';
import { getProjectById } from 'shared/src/project/Project.axios';

export const InvoicePaymentCreate: React.FC = () => {
  const navigate = useNavigate();
  const { axios, user } = useUser();
  const { t } = useTranslation();
  const location = useLocation<{ invoice?: Receipt; order?: Order }>();
  const [existingPayments, setExistingPayments] = useState<InvoicePayment[]>([]);
  const [project, setProject] = useState<Project>();
  const [company, setCompany] = useState<Company>();
  const [customer, setCustomer] = useState<Customer>();
  const [currency, setCurrency] = useState<Currency>();

  /**
   * Hook to load the customer and company of the receipt to check for the tax
   */
  useEffect(() => {
    if (!location.state?.order?.invoiceRecipient || !location.state.invoice?.projectId) return;
    Promise.all([
      getCustomerById(axios, location.state?.order?.invoiceRecipient.customerId),
      getCompanyById(axios, location.state.order.companyId),
      getProjectById(axios, location.state.invoice.projectId),
      getCurrencyById(axios, location.state.order.currencyId),
    ]).then(([loadedCustomer, loadedCompany, loadedProject, loadedCurrency]) => {
      loadedCompany && setCompany(loadedCompany);
      setCustomer(loadedCustomer);
      setProject(loadedProject);
      setCurrency(loadedCurrency);
    });
  }, [axios, location]);

  /**
   * The taxRate of the receipt
   */
  const taxRate: number = useMemo((): number => {
    if (!company || !customer) return 0;
    const hasTax: boolean = company.countryCode === customer?.contact?.countryCode;
    return hasTax ? company.taxRate / 100 : 0;
  }, [company, customer]);

  const invoiceToEdit: Receipt | undefined = useMemo(
    () => location.state?.invoice,
    [location.state?.invoice],
  );
  const [paymentToEdit, setPaymentToEdit] = useState<InvoicePayment>(
    generateEmptyInvoicePayment({
      invoiceId: location.state?.invoice?.id,
      orderId: location.state?.order?.id,
      customerId: location.state?.order?.customerId,
      createdBy: user.id,
    }),
  );
  const { data: loadedUsers } = useData('SCHAMBECK_USERS_ALL', {
    config: { fallbackData: [] },
  });
  const [showPartialPaymentPopup, toggleShowPaymentPopup] = useState<boolean>(false);

  /**
   *Hook to load all existing payments for the invoice
   */
  useEffect(() => {
    if (!location.state?.invoice?.id) return;
    getPaymentsForInvoice(axios, location.state.invoice.id).then(setExistingPayments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.invoice?.id, paymentToEdit.id]);
  /**
   * Helper method to complete a payment with the full sum of an invoice
   */
  const handlePayment = (type: 'partial' | 'complete'): void => {
    if (!invoiceToEdit) return;

    const payedAmount: number = existingPayments
      .map((payment) => payment.amount)
      .reduce((paymantOne, paymentTwo) => paymantOne + paymentTwo, 0);

    const updatedInvoice: Receipt =
      type === 'complete'
        ? {
            ...invoiceToEdit,
            invoiceState: InvoiceState.PAID,
          }
        : invoiceToEdit;

    const taxSum: number = receiptSum * taxRate;

    const newPayment: InvoicePayment =
      type === 'complete'
        ? {
            ...paymentToEdit,
            completePayment: true,
            amount: receiptSum + taxSum - payedAmount,
          }
        : paymentToEdit;

    Promise.all([
      updateInvoice(axios, updatedInvoice),
      createNewInvoicePayment(axios, newPayment),
    ]).then(([updatedInvoice, createdInvoicePayment]) => {
      if (updatedInvoice && createdInvoicePayment) {
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.createPayment'),
        });
        navigate('/invoice');
      }
    });
  };

  /**
   * Helper method to delete a payment and update local state
   */
  const handleDeletePayment = useCallback(
    async (id: string): Promise<void> => {
      const success: boolean = await deletePaymentById(axios, id);
      if (success) {
        const payments: InvoicePayment[] = existingPayments.filter((payment) => payment.id !== id);
        setExistingPayments(payments);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existingPayments],
  );

  /**
   * Holds the receiptSum without tax
   */
  const receiptSum: number = useMemo((): number => {
    if (!invoiceToEdit) return 0;
    return getTotalInvoiceSum(invoiceToEdit);
  }, [invoiceToEdit]);
  /**
   * Holds tableRows to display paments for the invoice
   */
  const paymentRows: TableRow[] = useMemo(
    (): TableRow[] =>
      convertExistingPaymentsIntoTableEntries(
        existingPayments,
        loadedUsers,
        setPaymentToEdit,
        handleDeletePayment,
      ),
    [existingPayments, handleDeletePayment, loadedUsers],
  );
  return (
    <>
      <TopBar onBackClick={() => navigate(-1)} title={t('pages.accounting.create.topBarHeadline')}>
        <Button
          type="button"
          value={t('general.buttons.completePayment')}
          onClick={() => handlePayment('complete')}
        />
        <Button
          type="button"
          onClick={() => toggleShowPaymentPopup(true)}
          value={t('general.buttons.partialPayment')}
        />
      </TopBar>
      <Popup
        isOpen={showPartialPaymentPopup}
        onClose={() => toggleShowPaymentPopup(false)}
        title={t('pages.customerUser.createOrEdit.partialPaymentPopupTitle')}
        buttons={[
          <Button
            type="button"
            value={t('general.buttons.cancel')}
            onClick={() => toggleShowPaymentPopup(false)}
          />,
          <Button
            type="button"
            value={t('general.buttons.save')}
            onClick={() => handlePayment('partial')}
          />,
        ]}
      >
        <p>{t('general.popup.partialPayment')}</p>
        <Input
          type="number"
          onChangeNumber={(amount) => setPaymentToEdit({ ...paymentToEdit, amount })}
          value={paymentToEdit.amount}
        />
      </Popup>
      {invoiceToEdit && (
        <Box title={t('pages.accounting.create.receiptInfo')}>
          <div className="three-columns">
            <Input
              type="text"
              disabled
              value={invoiceToEdit?.invoiceNumber}
              label={t('pages.accounting.create.receiptNumber')}
            />
            <Input
              type="text"
              disabled
              value={project?.numberRangeNumber}
              label={t('pages.accounting.create.projectNumber')}
            />
          </div>
          <div className="three-columns">
            <Input
              type="text"
              disabled
              value={receiptSum.toFixed(2) + ' ' + currency?.symbol}
              label={t('pages.accounting.create.sumPreTax')}
            />
            <Input
              type="text"
              disabled
              value={(receiptSum + receiptSum * taxRate).toFixed(2) + ' ' + currency?.symbol}
              label={t('pages.accounting.create.sumWithTax')}
            />
          </div>
        </Box>
      )}
      <Box title={t('pages.accounting.create.boxTitle')}>
        <div className="four-columns">
          <Input
            label={t('pages.accounting.create.paymentDate')}
            value={paymentToEdit.paymentDate}
            type="date"
            onChangeDate={(paymentDate) => {
              if (paymentDate) setPaymentToEdit({ ...paymentToEdit, paymentDate });
            }}
          />
          <Input
            type="number"
            value={invoiceToEdit?.dunningLevel}
            disabled
            onChangeNumber={() => {}}
            label={t('pages.accounting.create.dunningLevel')}
          />
          <Input
            disabled
            label={t('pages.accounting.create.createdBy')}
            type="text"
            value={getUserNameForSimpleUser(paymentToEdit.createdBy, loadedUsers)}
          />
          <TextArea
            resizable
            value={paymentToEdit?.paymentNote || ''}
            label={t('pages.accounting.create.paymentNote')}
            onChange={(paymentNote) => setPaymentToEdit({ ...paymentToEdit, paymentNote })}
          />
        </div>
      </Box>
      {existingPayments && (
        <Box title={t('pages.accounting.create.existingPayments')}>
          <Table
            rows={paymentRows}
            header={
              t('pages.accounting.create.tableHeader', {
                returnObjects: true,
              }) as TableHeader[]
            }
          />
        </Box>
      )}
    </>
  );
};
